import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';

import { ia } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';

import { NewRoleContext } from './NewRoleContext';

const NewRoleValidationSchema = Yup.object().shape({
  role_name: Yup.string().required('Role name is required')
});

export const NewRoleContextProvider = ({ children, forAdmin }) => {
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const [showNavigationButtons, setShowNavigationButtons] = useState(true);
  const [viewFormModal, setViewFormModal] = useState(false);
  const [enableNextTo, setEnableNextTo] = useState(false);
  const [newRoleSelectedUsers, setNewRoleSelectedUsers] = useState(0);
  const [enableSubmit, setEnableSubmit] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: practiceId } = useParams();

  useEffect(() => {
    if (newRoleSelectedUsers !== 0) {
      setEnableSubmit(true);
    } else {
      setEnableSubmit(false);
    }
  }, [newRoleSelectedUsers]);

  const initialBreadCrumbs = [
    { title: 'Role Details', view: 'role_details', active: true, isFinished: false },
    { title: 'Manage Permissions', view: 'manage_permissions', active: false, isFinished: false },
    { title: 'Assign Role', view: 'assign_role', active: false, isFinished: false }
  ];
  const [breadCrumbs, setBreadCrumbs] = useState(initialBreadCrumbs);

  const formik = useFormik({
    initialValues: {
      role_name: '',
      role_description: '',
      role_type: null,
      permissions: null,
      assignedUsers: []
    },
    validationSchema: NewRoleValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      handleCreate();
      setSubmitting(false);
    }
  });

  const handleClose = () => {
    setShowNewRoleModal(false);
    formik.resetForm();
    setBreadCrumbs(initialBreadCrumbs);
    setEnableNextTo(false);
    setNewRoleSelectedUsers(0);
  };

  const handleCreate = async () => {
    try {
      const res = await requestApi({
        url: `/api/permissions/create_and_assign`,
        params: {
          practice_id: parseInt(practiceId),
          forAdmin,
          ...formik.values
        },
        navigate
      });

      setShowNewRoleModal(false);
      formik.resetForm();
      setBreadCrumbs(initialBreadCrumbs);
      setEnableNextTo(false);
      setNewRoleSelectedUsers(0);
      queryClient.invalidateQueries(['getRoleList']);
      queryClient.invalidateQueries(['getRoleDescriptions']);
      queryClient.invalidateQueries(['getRole']);
      const { code, usersAssigned } = res;
      if (code === 0) {
        showAlert({
          title: ia(usersAssigned)
            ? 'Role created and assigned to the selected users'
            : 'Role created without assigning any users',
          color: 'success'
        });
      } else {
        showAlert({ title: "Couldn't create role", color: 'danger' });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  return (
    <NewRoleContext.Provider
      value={{
        formik,
        breadCrumbs,
        setBreadCrumbs,
        initialBreadCrumbs,
        enableNextTo,
        setEnableNextTo,
        setShowNewRoleModal,
        showNewRoleModal,
        handleClose,
        practiceId,
        showNavigationButtons,
        setShowNavigationButtons,
        viewFormModal,
        setViewFormModal,
        newRoleSelectedUsers,
        setNewRoleSelectedUsers,
        enableSubmit,
        setEnableSubmit
      }}>
      {children}
    </NewRoleContext.Provider>
  );
};
